'use client';

import Link from 'next/link';

import { Button, Typography } from 'lighthouse';

import { SafeImage } from '@/widgets/SafeImage';

import { rialToToman } from '@/utilities/Financial';
import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';
import { withSeparator } from '@/utilities/withSeparator';

import { resolveCatalogSpecs } from './mappers';
import { CatalogProductCardProps } from './models';

import { ArrowLeft, TickCircle } from 'iconsax-react';

const CatalogProductCard = ({
    item,
    showCategory = false,
    badge,
    priceNote,
    specs,
    isCompact = false,
    forceMobile = false,
    showSelectButton = true
}: CatalogProductCardProps) => {
    const imageFallback = item.isBattery ? '/images/battery/battery-placeholder.webp' : '/images/noImage/noImage.png';
    const imageSrc = GenerateFilePreviewUrl({
        attachableId: item.uuid,
        attachment: 'PRODUCT',
        type: 'MAIN',
        isPrimary: true,
        sortOrder: 0,
        width: 300,
        height: 300
    }).src;

    const mobileImageSrc = GenerateFilePreviewUrl({
        attachableId: item.uuid,
        attachment: 'PRODUCT',
        type: 'MAIN',
        isPrimary: true,
        sortOrder: 0,
        width: 200,
        height: 200
    }).src;

    const hasDiscount = !!(item.discountPercentage && item.sellingPrice && item.discountPercentage !== 99);
    const finalPrice = hasDiscount ? item.sellingPrice! : item.price!;
    // Specs (amperage, polarity, ...) are only meaningful for batteries; other categories show nothing until the backend supplies data.
    const resolvedSpecs = item.isBattery
        ? resolveCatalogSpecs({
              specs,
              attributes: item.attributes,
              subtitle: item.subtitle,
              guarantee: item.guarantee
          })
        : undefined;
    const specsText = resolvedSpecs?.filter(Boolean).join(' · ');

    const discountBadge = hasDiscount ? (
        <span
            dir='ltr'
            className={`shrink-0 rounded-full bg-amber-400 px-2 py-0.5 text-[10.5px] font-extrabold leading-4 text-blackHighEmphasis
                ${forceMobile ? '' : 'md:px-2.5 md:py-1 md:text-[11px]'}`}>
            {item.discountPercentage!.toLocaleString('fa-IR')}٪
        </span>
    ) : null;

    const offerPriceBlock = (
        <div className='min-w-0 text-left leading-none'>
            {hasDiscount && (
                <Typography variant='overline' component='p' color='blackLowEmphasis' className='mb-0 flex items-baseline justify-start leading-4'>
                    <span className='mr-1 shrink-0'>تومان</span>
                    <span className='truncate line-through'>{withSeparator(rialToToman(item.price!))}</span>
                </Typography>
            )}
            <Typography variant='h6' component='p' color='primary' fontWeight={800} className='mb-0 flex items-baseline justify-start leading-5'>
                <span className='mr-1 text-[11px] font-normal text-blackMediumEmphasis'>تومان</span>
                <span>{withSeparator(rialToToman(finalPrice))}</span>
            </Typography>
            {!!priceNote && (
                <Typography variant='overline' component='p' color='blackMediumEmphasis' className='mt-0.5 leading-4'>
                    {priceNote}
                </Typography>
            )}
        </div>
    );

    return (
        <article
            className={`group relative flex h-auto flex-col overflow-hidden rounded-2xl border border-solid border-N200 bg-white shadow-elevation1
                transition-all duration-300
                ${forceMobile ? '' : 'md:h-full md:hover:-translate-y-1 md:hover:border-primary/25 md:hover:shadow-elevation2'}
                ${!item.available ? 'opacity-80' : ''}`}>
            {/* Mobile: compact horizontal */}
            <div className={`flex min-h-[148px] flex-col gap-2 p-3 ${forceMobile ? '' : 'md:hidden'}`}>
                <Link href={item.detailsHref} className='flex gap-3'>
                    <span
                        className='relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-solid
                            border-N200 bg-white'>
                        <SafeImage width={56} height={56} src={mobileImageSrc} alt={item.title} fallback={imageFallback} className='object-contain' />
                    </span>

                    <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                        <Typography variant='overline' component='span' color='primary' className='font-medium leading-5'>
                            {item.brandName || '\u00A0'}
                        </Typography>
                        <Typography variant='subtitle2' component='h3' color='blackHighEmphasis' className='line-clamp-2 font-bold leading-5'>
                            {item.title}
                        </Typography>
                        {!!specsText && (
                            <Typography variant='overline' component='p' color='blackMediumEmphasis' className='line-clamp-1 leading-5'>
                                {specsText}
                            </Typography>
                        )}
                        {showCategory && item.categoryName && (
                            <Typography variant='overline' component='p' color='blackLowEmphasis' className='line-clamp-1 leading-5'>
                                {item.categoryName}
                            </Typography>
                        )}
                    </div>
                </Link>

                {!!badge && item.available && (
                    <div className='flex flex-wrap items-center gap-2'>
                        <span className='flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-bold text-primary'>
                            <TickCircle size={11} variant='Bold' color='currentColor' />
                            {badge}
                        </span>
                    </div>
                )}

                <div
                    dir={showSelectButton ? undefined : 'ltr'}
                    className={`flex items-center justify-between gap-2 border-0 border-t border-solid border-N100 pt-2
                        ${!item.available ? 'mt-auto min-h-[37px]' : ''}`}>
                    {item.available ? (
                        showSelectButton ? (
                            <>
                                <div className='min-w-0 leading-none'>
                                    {hasDiscount && (
                                        <div className='mb-0.5 flex items-center gap-1.5'>
                                            {discountBadge}
                                            <Typography
                                                variant='overline'
                                                component='span'
                                                color='blackLowEmphasis'
                                                className='leading-4 line-through'>
                                                {withSeparator(rialToToman(item.price!))} تومان
                                            </Typography>
                                        </div>
                                    )}
                                    <Typography variant='h6' component='span' color='primary' fontWeight={800} className='leading-5'>
                                        {withSeparator(rialToToman(finalPrice))}
                                        <span className='mr-1 text-[11px] font-normal text-blackMediumEmphasis'>تومان</span>
                                    </Typography>
                                    {!!priceNote && (
                                        <Typography variant='overline' component='p' color='blackMediumEmphasis' className='mt-0.5 leading-4'>
                                            {priceNote}
                                        </Typography>
                                    )}
                                </div>
                                <Link href={item.buyHref} className='shrink-0 self-center'>
                                    <Button
                                        size='small'
                                        variant='contained'
                                        color='primary'
                                        className='!min-h-0 cursor-pointer whitespace-nowrap py-1.5 text-white'>
                                        انتخاب
                                    </Button>
                                </Link>
                            </>
                        ) : (
                            <>
                                {offerPriceBlock}
                                {discountBadge}
                            </>
                        )
                    ) : (
                        <Typography variant='body2' component='p' color='blackLowEmphasis' className='w-full text-center text-xs font-medium'>
                            ناموجود
                        </Typography>
                    )}
                </div>
            </div>

            {/* Desktop: tall catalog card */}
            <div className={`hidden h-full flex-col ${forceMobile ? '' : 'md:flex'}`}>
                {showSelectButton && hasDiscount && item.available && (
                    <span
                        className='absolute right-3 top-3 z-10 rounded-full bg-amber-400 px-2.5 py-1 text-[11px] font-extrabold text-blackHighEmphasis
                            shadow-sm'>
                        {item.discountPercentage!.toLocaleString('fa-IR')}٪
                    </span>
                )}

                {item.available && !!badge && (
                    <span className='absolute left-3 top-3 z-10 rounded-full bg-primary px-2.5 py-1 text-[11px] font-bold text-white shadow-sm'>
                        {badge}
                    </span>
                )}

                <Link href={item.detailsHref} className='flex flex-1 flex-col'>
                    <div className={`relative flex items-center justify-center overflow-hidden bg-white ${isCompact ? 'h-48' : 'h-64'}`}>
                        <SafeImage
                            width={isCompact ? 170 : 220}
                            height={isCompact ? 170 : 220}
                            src={imageSrc}
                            alt={item.title}
                            fallback={imageFallback}
                            className='relative object-contain transition-transform duration-300 group-hover:scale-105'
                        />
                    </div>

                    <div className='flex flex-1 flex-col gap-2 px-4 pb-3 pt-1'>
                        <Typography variant='overline' component='p' color='primary' className='font-medium'>
                            {item.brandName || '\u00A0'}
                        </Typography>
                        <Typography
                            variant='subtitle2'
                            component='h3'
                            color='blackHighEmphasis'
                            className='line-clamp-2 min-h-10 font-bold leading-5'>
                            {item.title}
                        </Typography>
                        {!!resolvedSpecs?.length && (
                            <ul className={`m-0 flex list-none flex-wrap content-start gap-1 p-0 ${isCompact ? '' : 'min-h-[3.25rem]'}`}>
                                {resolvedSpecs.map((spec) => (
                                    <li key={spec} className='h-fit rounded-md bg-N100 px-2 py-0.5 text-[10.5px] text-blackMediumEmphasis'>
                                        {spec}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {showCategory && item.categoryName && (
                            <Typography variant='caption' component='p' color='blackLowEmphasis' className='mt-auto line-clamp-1'>
                                {item.categoryName}
                            </Typography>
                        )}
                    </div>
                </Link>

                <div
                    dir={showSelectButton ? undefined : 'ltr'}
                    className='mt-auto flex items-center justify-between gap-3 border-t border-N100 bg-N50/70 px-4 py-3'>
                    {item.available ? (
                        showSelectButton ? (
                            <>
                                <div className='min-w-0'>
                                    {hasDiscount && (
                                        <Typography variant='overline' component='p' color='blackLowEmphasis' className='line-through'>
                                            {withSeparator(rialToToman(item.price!))} تومان
                                        </Typography>
                                    )}
                                    <Typography variant='h6' component='span' color='primary' fontWeight={800} className='leading-6'>
                                        {withSeparator(rialToToman(finalPrice))}
                                        <span className='mr-1 text-[11px] font-normal text-blackMediumEmphasis'>تومان</span>
                                    </Typography>
                                    {!!priceNote && (
                                        <Typography variant='overline' component='p' color='blackMediumEmphasis' className='leading-5'>
                                            {priceNote}
                                        </Typography>
                                    )}
                                </div>
                                <Link href={item.buyHref} className='shrink-0'>
                                    <Button
                                        size='small'
                                        variant='contained'
                                        color='primary'
                                        endIcon={<ArrowLeft size={16} color='currentColor' />}
                                        className='cursor-pointer text-white'>
                                        انتخاب
                                    </Button>
                                </Link>
                            </>
                        ) : (
                            <>
                                {offerPriceBlock}
                                {discountBadge}
                            </>
                        )
                    ) : (
                        <Typography variant='body2' component='p' color='blackLowEmphasis' className='w-full text-center font-medium'>
                            ناموجود
                        </Typography>
                    )}
                </div>
            </div>
        </article>
    );
};

export default CatalogProductCard;
